/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useRef } from 'react'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import * as yup from 'yup'

import { Accordion, Form, FormButtons, TextArea, TextInput } from '@/components'
import { useApp } from '@/hooks'
import { useForm } from '@/hooks/index'
import { t } from '@/i18n'
import CustomerSelector from '@/pages/Customer/customerSelector'
import ProposalCarFormComponents from '@/pages/Proposal/FormComponents/ProposalCarFormComponents'
import ProposalInstallationFormComponents from '@/pages/Proposal/FormComponents/ProposalInstallationFormComponents'
import ProposalPriceFormComponents from '@/pages/Proposal/FormComponents/ProposalPriceFormComponents'
import { IProposal } from '@/types'
import { addDealPath } from '@/utils'
import moment from 'moment'

interface Props {
  isEditing?: boolean
  initialData?: Partial<IProposal & { installations?: any[] }>
  loading: boolean
  submitting: boolean
  onCancel: () => void
  onSubmit: (d: any) => void
}

const ProposalForm: React.FC<Props> = ({
  isEditing,
  initialData,
  loading,
  submitting,
  onCancel,
  onSubmit,
}) => {
  const { currency } = useApp()
  const navigate: NavigateFunction = useNavigate()
  const formRef = useRef<HTMLFormElement | null>(null)

  const schema = useMemo(
    () =>
      yup
        .object({
          leaseDuration: yup.number().required(t('required_field_error')),
          customer: yup.number().required(t('required_field_error')),
          carPrice: yup.number().required(t('required_field_error')),
          carSubModel: yup.number().required(t('required_field_error')),
          baseIndex: yup.number().required(t('required_field_error')),
          modelYear: yup.number().required(t('required_field_error')),
          installations: yup
            .array()
            .of(
              yup.object({
                name: yup.string().required(t('required_field_error')),
                price_without_vat: yup
                  .string()
                  .required(t('required_field_error')),
                installation_type: yup
                  .string()
                  .required(t('required_field_error')),
                is_customer_paid: yup.boolean(),
              }),
            )
            .default([]),
        })
        .required(),
    [],
  )

  const { control, isValid, getValues, onTriggerChange, onFormSubmit, watch } =
    useForm<any>({
      isEditing,
      defaultValues: initialData,
      schema,
      onSubmit,
    })
  const modelYear = watch('modelYear')
  useEffect(() => {
    if (!modelYear) onTriggerChange('modelYear', moment().year())
  }, [modelYear, onTriggerChange])

  useEffect(() => {
    if (initialData?.customer) {
      onTriggerChange('customer', initialData.customer.id)
    }
  }, [initialData?.customer, onTriggerChange])
  const emptyValue = useMemo(() => (isEditing ? null : '...'), [isEditing])

  return (
    <div className="edit-info form-fields">
      {loading ? (
        <div className="data-box">
          <div className="loading-text">{t('loading')}</div>
        </div>
      ) : (
        <>
          <Form<any>
            className={isEditing ? 'editing' : 'view'}
            isEditing={isEditing}
            currency={getValues('currency') || currency}
            formRef={formRef}
            control={control}
            onSubmit={onFormSubmit}
          >
            <Accordion isFormElement title={t('price_info')} preExpanded>
              {(props: any) => (
                <ProposalPriceFormComponents
                  {...props}
                  initialData={initialData}
                  onTriggerChange={onTriggerChange}
                  isFormElement
                  emptyValue={emptyValue}
                  getValues={getValues}
                  watch={watch}
                  isEditing={isEditing}
                />
              )}
            </Accordion>
            <Accordion isFormElement title={t('car_details')} preExpanded>
              {(props: any) => (
                <ProposalCarFormComponents
                  {...props}
                  isFormElement
                  initialData={initialData}
                  loading={loading}
                  onTriggerChange={onTriggerChange}
                  emptyValue={emptyValue}
                  isEditing={isEditing}
                />
              )}
            </Accordion>
            <Accordion isFormElement title={t('installations')} preExpanded>
              {(props: any) => (
                <ProposalInstallationFormComponents
                  {...props}
                  isFormElement
                  control={control}
                  isEditing={isEditing}
                  emptyValue={emptyValue}
                />
              )}
            </Accordion>
            <TextInput
              isEditing={isEditing}
              required
              className="w-33"
              control={control}
              name="leaseDuration"
              type="number"
              emptyValue={emptyValue}
              disabled={!Boolean(isEditing)}
              label={t('lease_duration')}
              min={0}
            />
            <CustomerSelector
              required
              isFormElement
              className="w-50"
              control={control}
              isEditing={isEditing}
              initial={initialData?.customer}
              selected={getValues('customer')}
            />
            <TextArea
              isEditing={isEditing}
              name="notes"
              rows={3}
              className="w-50"
              control={control}
              emptyValue={emptyValue}
              disabled={!Boolean(isEditing)}
              label={t('notes')}
            />
            <TextInput
              isEditing={isEditing}
              control={control}
              name="isOperational"
              type="checkbox"
              label={t('is_operational')}
              emptyValue={emptyValue}
              disabled={!Boolean(isEditing)}
            />
            <TextInput
              isEditing={isEditing}
              control={control}
              name="isLocked"
              type="checkbox"
              emptyValue={emptyValue}
              disabled={!Boolean(isEditing)}
              label={t('is_locked')}
            />
          </Form>

          <FormButtons
            visible={isEditing}
            formRef={formRef}
            isFormValid={isValid}
            submitting={submitting}
            onCancel={onCancel}
          />
          {!isEditing && (
            <div className="btn-box">
              <button
                type="button"
                disabled={!isValid || submitting}
                className="main-btn success"
                onClick={() =>
                  navigate(`${addDealPath}?proposal=${initialData?.id || ''}`)
                }
              >
                {t('create_deal_from_proposal')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default ProposalForm
